import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
// Assuming DataTable is correctly imported from your components directory
import DataTable from "../components/DataTable";
import config from "../config";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete"; // Required for actions

const baseUrl = config.baseURL;

// ----------------------------------------------------
// --- DynamicModulePage Component ---
// ----------------------------------------------------
const DynamicModulePage = ({ bottomNavHeight = 60 }) => {
  const { moduleSlug } = useParams();

  // State to hold the module configuration fetched from the backend
  const [moduleConfig, setModuleConfig] = useState(null);
  // State for permission status
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false); // Added for better control over buttons

  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleId, setModuleId] = useState(null);

  // Pagination and sorting states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const itemsPerPage = 10;
  const safeSlug = moduleSlug
    ? moduleSlug
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
    : "";

  const token = localStorage.getItem("token");
  // Filter state
  const [filters, setFilters] = useState({});
  console.log("moduleSlug from URL:", moduleSlug);
  console.log("safeSlug from URL:", safeSlug);

  /**
   * Checks the user's read and write permissions for the module.
   */
  const checkPermissions = useCallback(async () => {
    const token = localStorage.getItem("token");
    // Check Read Permission
    // const safeSlug = (moduleSlug || "").trim().replace(/\s+/g, "_");
    // const safeSlug = moduleSlug
    //   ? moduleSlug.trim().toLowerCase().replace(/\s+/g, "-")
    //   : "";

    console.log("safeSlug", safeSlug);
    const readPermissionUrl = `${baseUrl}/api/module-role-permissions?moduleId=${safeSlug}&action=read`;

    const writePermissionUrl = `${baseUrl}/api/module-role-permissions?moduleId=${safeSlug}&action=write`;

    let readAccess = false;
    let writeAccess = false;

    try {
      const [readResponse, writeResponse] = await Promise.all([
        axios.get(readPermissionUrl, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(writePermissionUrl, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // Determine Read Access
      readAccess =
        (readResponse.data.data && readResponse.data.data.length > 0) ||
        readResponse.data.hasPermission === true;
      setCanRead(readAccess);

      // Determine Write Access
      writeAccess =
        (writeResponse.data.data && writeResponse.data.data.length > 0) ||
        writeResponse.data.hasPermission === true;
      setCanWrite(writeAccess);

      if (!readAccess) {
        toast.error(
          "Access Denied: You do not have permission to view this module's data."
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error(
        `Failed to check permissions for module ${moduleSlug}:`,
        error
      );
      toast.error("Failed to verify access permissions.");
      setCanRead(false);
      setCanWrite(false);
      return false;
    }
  }, [moduleSlug]);
  useEffect(() => {
    const fetchFields = async () => {
      if (!moduleId) return; // Wait until we know moduleId
      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${baseUrl}/api/module/${moduleId}/fields`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const fields = res.data.data || res.data || [];
        console.log("Fetched fields:", fields);

        // Default "ID" column
        const defaultIdColumn = {
          header: "ID",
          accessor: "id",
          type: "showOnlyText",
        };

        // Define mapping for backend data types
        const typeMapping = {
          text: "text",
          number: "number",
          decimal: "number",
          integer: "number",
          float: "number",
          date: "Date",
          datetime: "DateTime",
          boolean: "checkbox",
          dropdown: "dropdown",
          file: "fileUpload",
          json: "jsonViewer",
          list: "listItems",
        };

        // Define mapping for display types (UI preference overrides)
        const displayMapping = {
          dropdown: "dropdown",
          textarea: "TextDetails",
          switch: "checkbox",
          file: "fileUpload",
          image: "imagePreview",
        };

        // Map backend fields to DataTable columns
        const dynamicColumns = fields
          .map((field) => {
            const accessor =
              field.accessor || field.name || field.label || null;

            if (!accessor) return null; // ❌ skip invalid

            return {
              header: field.label || field.name || accessor,
              accessor,
              type: field.data_type === "text" ? "text" : "input",
            };
          })
          .filter(Boolean); // remove null entries

        const columns = [defaultIdColumn, ...dynamicColumns];
        const currentModule = modules.find(
          (m) => m.slug.toLowerCase() === safeSlug.toLowerCase()
        );

        setModuleConfig({
          title: `Module Data: ${moduleSlug}`,
          apiEndpoint: `/api/module-data/${moduleId}`,
          table_name: currentModule.table_name,
          columns,
        });
      } catch (error) {
        console.error("Failed to fetch fields:", error);
      }
    };

    fetchFields();
  }, [moduleId]);

  /**
   * Fetches the module's field definitions from the backend.
   */
  const fetchModuleConfig = useCallback(async () => {
    setLoading(true);

    // 1. Check Permission First
    const hasPermission = await checkPermissions();
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    const modulesResponse = await axios.get(`${baseUrl}/api/module`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const modules = modulesResponse.data.data;

    const currentModule = modules.find(
      (m) => m.slug.toLowerCase() === safeSlug.toLowerCase()
    );
    console.log("modules", modules);
    console.log("modules safeSlug", safeSlug);
    console.log("modules currentModule", currentModule);

    if (!currentModule) {
      console.error("Module not found for slug:", safeSlug);
      return;
    }

    const moduleId = currentModule.id;
    setModuleId(moduleId); // ✅ This is the missing piece

    // Now fetch fields using moduleId
    const fieldsUrl = `${baseUrl}/api/module/${moduleId}/fields`;

    //   const fieldsResponse = await axios.get(fieldsUrl, {
    //     headers: { Authorization: `Bearer ${token}` },
    //   });
    // const fieldsUrl = `${baseUrl}/api/module/${safeSlug}/fields`;
    let columns = [];
    try {
      const response = await axios.get(fieldsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("fieldsss response", response);
      const fields = response.data.data || response.data;
      console.log("fieldsss", fields);
      const defaultIdColumn = {
        header: "ID",
        accessor: "id",
        type: "showOnlyText",
      };

      const dynamicColumns = fields.map((field) => ({
        header: field.label || field.name || field.accessor,
        accessor: field.accessor,
        type: field.data_type === "text" ? "text" : "input",
      }));

      const allColumns = [defaultIdColumn, ...dynamicColumns];

      setModuleConfig({
        title: `Module Data: ${moduleSlug}`,
        apiEndpoint: `/api/module-data/${safeSlug}`, // NOT module-records
        table_name: currentModule.table_name,
        columns: allColumns,
      });

      // Reset states for the newly loaded module
      setCurrentPage(1);
      setFilters({});
    } catch (error) {
      console.error(`Failed to fetch fields for module ${moduleSlug}:`, error);
      toast.error(`Failed to load module structure for ${moduleSlug}.`);

      // Set a minimal error configuration
      setModuleConfig({
        title: `Module Data: ${moduleSlug}`,
        apiEndpoint: `/api/module-data/${safeSlug}`,
        table_name: currentModule.table_name,
        columns: columns,
      });
    } finally {
      // setLoading is deferred to fetchData or set by checkPermissions on fail
    }
  }, [moduleSlug, checkPermissions]);

  /**
   * Fetches the actual table data using the dynamically loaded moduleConfig
   * via the consolidated endpoint.
   */
  // Add this function inside DynamicModulePage

  const fetchData = useCallback(
    async (
      page = 1,
      pageSize = itemsPerPage,
      currentFilters = filters,
      field = sortField,
      order = sortOrder
    ) => {
      if (!moduleConfig || !moduleConfig.apiEndpoint || !canRead) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const token = localStorage.getItem("token");

      const safeSlug = moduleSlug
        ? moduleSlug
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, "_")
        : "";

      let apiUrl = `${baseUrl}/api/module-data/${safeSlug}?page=${page}&limit=${pageSize}&sort=${field}&order=${order}`;
      console.log("resposne in apiUrl", apiUrl);
      console.log("resposne in safeSlug", safeSlug);
      console.log("resposne in moduleSlug", moduleSlug);

      // ✅ Add filters safely
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value) {
          const filterValue = Array.isArray(value) ? value.join(",") : value;
          apiUrl += `&${key}=${encodeURIComponent(filterValue)}`;
        }
      });

      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("response raw:", response);
        console.log("response.data:", response?.data);

        const {
          data: fetchedData,
          totalPages: apiTotalPages,
          totalItems: apiTotalItems,
          allData: apiAllData,
        } = response.data;
        setData(fetchedData || []);
        setTotalPages(apiTotalPages || 1);
        setTotalItems(apiTotalItems || (fetchedData ? fetchedData.length : 0));
        setAllData(apiAllData || fetchedData || []);
      } catch (error) {
        console.log("Axios error:", error.response?.data || error.message);

        if (error.response && error.response.status === 403) {
          toast.error(
            "Access Denied: Your read permission was revoked or expired."
          );
          setCanRead(false);
        }
        console.error(`Failed to fetch data for module ${moduleSlug}:`, error);
        toast.error(`Failed to load ${moduleConfig.title} data.`);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [moduleSlug, moduleConfig, filters, sortField, sortOrder, canRead, moduleId]
  );

  // 1. Initial configuration and permission fetch
  useEffect(() => {
    fetchModuleConfig();
  }, [fetchModuleConfig]);

  // 2. Data fetch runs whenever config is available and query parameters change
  useEffect(() => {
    // Only fetch data if module config is loaded AND read permission is granted
    if (moduleConfig && canRead) {
      fetchData(currentPage, itemsPerPage);
    }
  }, [moduleConfig, currentPage, sortField, sortOrder, canRead, fetchData]);

  // --- Data Table Handler Functions ---

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (field) => {
    const order = field === sortField && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };
  const handleDeleteRow = async (row) => {
    if (!row.id) {
      toast.error("Cannot delete: Record ID not found.");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete record ID ${row.id}?`
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${baseUrl}/api/module-data/${safeSlug}/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(`✅ Record ID ${row.id} deleted successfully!`);
      setData((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      console.error("Delete Error:", err.response || err.message);
      toast.error(
        `❌ Failed to delete record: ${
          err.response?.data?.error || "Server error"
        }`
      );
    }
  };

  const handleAddRow = async () => {
    if (!canWrite || !moduleConfig?.apiEndpoint) return;

    // 1. Initialize newRow with 'null' for all default values, except a default for the main field.
    const newRow = {};
    const dynamicColumns = moduleConfig.columns.filter(
      (col) => col.accessor && col.accessor !== "id"
    );

    // Identify the main field (first dynamic column)
    const mainField = dynamicColumns[0]; // Assuming first dynamic column is the "main" one

    dynamicColumns.forEach((col) => {
      if (col.accessor === "id") return;

      if (col === mainField) {
        // Set default value for main field
        if (col.type === "number") {
          newRow[col.accessor] = 1; // Default number
        } else {
          newRow[col.accessor] = "New Record"; // Default text (adjust as needed)
        }
      } else {
        // Allow null for others
        newRow[col.accessor] = null;
      }
    });

    // 2. Show instantly (optimistic UI)
    setData((prev) => [newRow, ...prev]);

    try {
      const token = localStorage.getItem("token");
      const payloadData = {};

      // 2. Iterate over the newRow to clean and prepare for the API
      for (const key in newRow) {
        if (!key || key === "undefined" || key === "null") continue; // skip invalid keys
        const value = newRow[key];
        if (value === "" || value === undefined) {
          payloadData[key] = null;
        } else {
          payloadData[key] = value;
        }
      }
      console.log("payloadData neww row", payloadData);
      // The payload only needs the data object
      const res = await axios.post(
        `${baseUrl}/api/module-data/${moduleSlug}`,
        { data: payloadData }, // Sending the new row data
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("rres neww row", res);
      toast.success("✅ Row added successfully!");

      // 3. Navigate to the last page where the new record should appear
      const newTotalItems = totalItems + 1;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (newTotalPages > totalPages) {
        // If a new page was created, navigate there
        setCurrentPage(newTotalPages);
      }

      // 4. Fetch data to replace the temporary row with the DB record (including its new ID)
      fetchData();
    } catch (err) {
      console.error("API POST Error:", err.response?.data || err.message);
      toast.error(
        "❌ Failed to add record: " +
          (err.response?.data?.details || "Server error.")
      );
      // 5. Rollback the optimistic update
      setData((prev) => prev.filter((row) => row !== newRow));
    }
  };

  const getRangeInputProps = (col) => ({
    placeholder: `Range for ${col.header}`,
  });

  // --- Render Logic ---
  // Define actions for the DataTable, only allowing delete if the user can write
  const actions = useMemo(
    () =>
      canWrite
        ? [
            {
              name: "CustomDelete", // renamed so DataTable's default delete is NOT triggered
              icon: <DeleteIcon color="error" />,
              onClick: handleDeleteRow,
            },
          ]
        : [],
    [canWrite, safeSlug, handleDeleteRow]
  );

  if (loading && !moduleConfig) {
    return (
      <div style={{ padding: "32px" }}>
        <p>Loading Module Structure and Checking Permissions...</p>
      </div>
    );
  }

  // Display access denied message
  if (!canRead && !loading) {
    return (
      <div style={{ padding: "32px" }}>
        <h2>Access Denied 🔒</h2>
        <p>
          You do not have the required permissions to view the data for Module:
          **{moduleSlug}**.
        </p>
      </div>
    );
  }

  if (!moduleConfig) {
    // If loading finished but moduleConfig is still null (error state)
    return (
      <div style={{ padding: "32px" }}>
        <p>Module configuration failed to load. Check console for errors.</p>
      </div>
    );
  }

  return (
    <DataTable
      title={moduleConfig.title}
      columns={moduleConfig.columns} // Dynamically provided columns
      data={data}
      setData={setData}
      onAddRow={handleAddRow}
      onSort={handleSort}
      filters={filters}
      onFilterChange={setFilters}
      sortField={sortField}
      sortOrder={sortOrder}
      actions={actions}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      apiEndpoint={moduleConfig.apiEndpoint} // Consolidated endpoint
      itemsPerPage={itemsPerPage}
      fetchData={fetchData}
      getRangeInputProps={getRangeInputProps}
      showAddButton={canWrite} // Only show add button if user has write permission
      allData={allData}
      totalItems={totalItems}
      bottomNavHeight={bottomNavHeight}
    />
  );
};

export default DynamicModulePage;
